import React, { useEffect, useRef, useState } from 'react';
import { Eraser, Save, Trash2, Upload } from 'lucide-react';

/**
 * Lightweight HTML-canvas signature pad.
 * - Captures mouse + touch + pen events
 * - Outputs a PNG data-URL via onChange
 * - Accepts an initial value (data-URL) which is rendered into the canvas
 * - Optional "Save as default" + "Use saved" buttons (controlled by props)
 */
export const SignaturePad = ({
  value,
  onChange,
  onSaveDefault,
  onUseDefault,
  onClearDefault,
  hasDefault = false,
  height = 140,
  testid = 'signature-pad',
}) => {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const last = useRef({ x: 0, y: 0 });
  const [hasInk, setHasInk] = useState(false);

  // Re-render the incoming value when it changes
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    // Set canvas to its CSS size for crisp rendering (HiDPI safe)
    const dpr = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;
    ctx.scale(dpr, dpr);
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = '#0f172a';
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, rect.width, rect.height);
    setHasInk(false);

    if (value) {
      const img = new Image();
      img.onload = () => {
        ctx.drawImage(img, 0, 0, rect.width, rect.height);
        setHasInk(true);
      };
      img.src = value;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const getPos = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const t = e.touches?.[0];
    return {
      x: (t ? t.clientX : e.clientX) - rect.left,
      y: (t ? t.clientY : e.clientY) - rect.top,
    };
  };

  const start = (e) => {
    e.preventDefault();
    drawing.current = true;
    last.current = getPos(e);
  };

  const move = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext('2d');
    const p = getPos(e);
    ctx.beginPath();
    ctx.moveTo(last.current.x, last.current.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    last.current = p;
    setHasInk(true);
  };

  const end = () => {
    if (!drawing.current) return;
    drawing.current = false;
    if (onChange) onChange(canvasRef.current.toDataURL('image/png'));
  };

  const clear = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, rect.width, rect.height);
    setHasInk(false);
    if (onChange) onChange(null);
  };

  const onUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const url = ev.target.result;
      if (onChange) onChange(url);
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  return (
    <div data-testid={testid} className="space-y-2">
      <div
        className="relative bg-white border-2 border-dashed border-border rounded-lg overflow-hidden"
        style={{ height }}
      >
        <canvas
          ref={canvasRef}
          className="w-full h-full touch-none cursor-crosshair"
          onMouseDown={start}
          onMouseMove={move}
          onMouseUp={end}
          onMouseLeave={end}
          onTouchStart={start}
          onTouchMove={move}
          onTouchEnd={end}
          data-testid={`${testid}-canvas`}
        />
        {!hasInk && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <span className="text-sm text-foreground/40 italic">Sign here</span>
          </div>
        )}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={clear}
          data-testid={`${testid}-clear`}
          className="px-3 py-1.5 text-xs border border-border rounded hover:bg-gray-50 flex items-center gap-1"
        >
          <Eraser className="w-3 h-3" /> Clear
        </button>
        <label className="px-3 py-1.5 text-xs border border-border rounded hover:bg-gray-50 flex items-center gap-1 cursor-pointer">
          <Upload className="w-3 h-3" /> Upload image
          <input type="file" accept="image/png,image/jpeg" className="hidden" onChange={onUpload} />
        </label>
        {onSaveDefault && (
          <button
            type="button"
            disabled={!hasInk}
            onClick={() => onSaveDefault(canvasRef.current.toDataURL('image/png'))}
            data-testid={`${testid}-save-default`}
            className="px-3 py-1.5 text-xs bg-emerald-600 text-white rounded hover:bg-emerald-700 disabled:opacity-50 flex items-center gap-1"
          >
            <Save className="w-3 h-3" /> Save as default
          </button>
        )}
        {hasDefault && onUseDefault && (
          <button
            type="button"
            onClick={onUseDefault}
            data-testid={`${testid}-use-default`}
            className="px-3 py-1.5 text-xs border-2 border-emerald-500 text-emerald-700 rounded hover:bg-emerald-50"
          >
            Use saved signature
          </button>
        )}
        {hasDefault && onClearDefault && (
          <button
            type="button"
            onClick={onClearDefault}
            data-testid={`${testid}-clear-default`}
            className="px-3 py-1.5 text-xs text-red-600 hover:bg-red-50 rounded flex items-center gap-1"
          >
            <Trash2 className="w-3 h-3" /> Remove saved
          </button>
        )}
      </div>
    </div>
  );
};

export default SignaturePad;
